/**
 * Sistem Bildirimi Popup Component
 * Kompakt bildirim popup'ı - resim destekli
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, Volume2, Check, AlertTriangle, Info, Megaphone, User, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIP_CONFIG = {
  bilgi: { icon: Info, bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600' },
  guncelleme: { icon: Bell, bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600' },
  uyari: { icon: AlertTriangle, bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600' },
  acil: { icon: Megaphone, bg: 'bg-red-500', light: 'bg-red-50', text: 'text-red-600' }
};

const NOTIFICATION_SOUND = '/sounds/alert.wav';  // alert.mp3 yoktu (404); sistem uyarı sesi

const SistemBildirimPopup = ({ userRole }) => {
  const [bildirimler, setBildirimler] = useState([]);
  const [aktifBildirim, setAktifBildirim] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [resimBuyuk, setResimBuyuk] = useState(false);
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);
  
  // Müşteriler için bildirim kontrolü yapma
  const isMusteri = userRole === 'musteri' || userRole === 'client';

  const playSound = useCallback(() => {
    try { 
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        audioRef.current.volume = 0.7;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Audio play hatası - sessizce devam et
      }); 
    } catch (err) {
      // Ses çalma hatası
    }
  }, []);

  const checkBildirimler = useCallback(async () => {
    // Müşteriler bildirim almaz
    if (isMusteri) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/panel/sistem-bildirimleri/bekleyen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.bildirimler?.length > 0) {
        setBildirimler(response.data.bildirimler);
        if (!aktifBildirim) {
          const ilk = response.data.bildirimler[0];
          setAktifBildirim(ilk);
          setAnimating(true);
          if (ilk.sesli) playSound();
        }
      }
    } catch (e) { console.debug('Bildirim:', e.message); }
  }, [aktifBildirim, isMusteri, playSound]);

  const handleGordum = async () => {
    if (!aktifBildirim) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/panel/sistem-bildirimleri/gordum`, { bildirim_id: aktifBildirim.id }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const kalan = bildirimler.filter(b => b.id !== aktifBildirim.id);
      setBildirimler(kalan);
      if (kalan.length > 0) {
        setAktifBildirim(kalan[0]);
        if (kalan[0].sesli) playSound();
      } else {
        setAktifBildirim(null);
        setAnimating(false);
      }
    } catch (e) { console.error('Hata:', e); }
  };

  useEffect(() => {
    // İlk yükleme için timeout ile cascade render önlenir
    const timeoutId = setTimeout(() => {
      checkBildirimler();
    }, 500);
    
    checkIntervalRef.current = setInterval(checkBildirimler, 30000);
    return () => { 
      clearTimeout(timeoutId);
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current); 
    };
  }, [checkBildirimler]);

  const formatTarih = (t) => {
    if (!t) return '';
    const d = new Date(t), now = new Date(), diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Az önce';
    if (diff < 60) return `${diff} dk önce`;
    if (diff < 1440) return `${Math.floor(diff / 60)} saat önce`;
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (!aktifBildirim) return <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />;

  const tipConfig = TIP_CONFIG[aktifBildirim.tip] || TIP_CONFIG.bilgi;
  const TipIcon = tipConfig.icon;
  const gonderen = aktifBildirim.olusturan?.ad || aktifBildirim.olusturan?.email || 'Sistem';

  return (
    <>
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />
      
      <div className="fixed inset-0 bg-black/25 backdrop-blur-[1px] z-[9999] flex items-center justify-center p-3">
        <div className={`bg-white rounded-xl shadow-xl w-full max-w-xs overflow-hidden transform transition-all duration-200 ${animating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          
          {/* Header - Kompakt */}
          <div className={`${tipConfig.light} px-3 py-2 border-b border-gray-100`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 ${tipConfig.bg} rounded-md flex items-center justify-center`}>
                <TipIcon size={14} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] font-semibold ${tipConfig.text}`}>
                  {aktifBildirim.tip === 'bilgi' && 'Bilgi'}
                  {aktifBildirim.tip === 'guncelleme' && 'Güncelleme'}
                  {aktifBildirim.tip === 'uyari' && 'Uyarı'}
                  {aktifBildirim.tip === 'acil' && 'Acil'}
                </p>
                <div className="flex items-center gap-1.5 text-[9px] text-gray-400">
                  <User size={8} /><span>{gonderen}</span>
                  <span>•</span>
                  <Clock size={8} /><span>{formatTarih(aktifBildirim.created_at)}</span>
                </div>
              </div>
              {aktifBildirim.sesli && <Volume2 size={12} className={`${tipConfig.text} animate-pulse`} />}
            </div>
          </div>

          {/* İçerik */}
          <div className="px-3 py-2.5">
            <h3 className="text-[11px] font-semibold text-gray-800 mb-1">{aktifBildirim.baslik}</h3>
            <p className="text-[10px] text-gray-600 leading-relaxed">{aktifBildirim.mesaj}</p>
            
            {/* Resim varsa */}
            {aktifBildirim.resim && (
              <div className="mt-2">
                <img 
                  src={aktifBildirim.resim} 
                  alt="Bildirim" 
                  className="w-full rounded-md cursor-pointer hover:opacity-90 transition max-h-32 object-cover border border-gray-100"
                  onClick={() => setResimBuyuk(true)}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 pb-3">
            {bildirimler.length > 1 && (
              <p className="text-center text-[9px] text-gray-400 mb-2">+{bildirimler.length - 1} bildirim daha</p>
            )}
            <button onClick={handleGordum}
              className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 ${tipConfig.bg} text-white rounded-lg text-[10px] font-medium hover:opacity-90 transition`}>
              <Check size={12} /> Gördüm
            </button>
          </div>
        </div>
      </div>

      {/* Büyük Resim Modal */}
      {resimBuyuk && aktifBildirim.resim && (
        <div className="fixed inset-0 bg-black/80 z-[10000] flex items-center justify-center p-4" onClick={() => setResimBuyuk(false)}>
          <img src={aktifBildirim.resim} alt="Bildirim" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
};

export default SistemBildirimPopup;
